// Anteprima schematica del manifesto (non AI): un foglio CSS con zone,
// centro riservato, tinte palette, watermark scena e overlay cornice.

import {
  BUILDER_ZONES,
  BUILDER_SCENES,
  BUILDER_PALETTES,
  FRAME_STYLES,
  GRADIENT_DIRECTIONS,
  findByKey,
  SCENE_PLACEMENTS,
  SCENE_EXTENTS,
  SACRED_SCENE_KEY,
  findSacredFigure,
  isGeometricFrameStyle,
} from './builderCatalog';
import { elementIconSvg } from './builderIcons';
import { gradientHandlePosition } from './blueprintInteractions';
import { resolvedGradientEndpoints } from './builderState';

export const DEFAULT_RATIOS = {
  vertical: '707 / 1000',
  horizontal: '1000 / 707',
};

const GRADIENT_ANGLES = {
  top: '180deg',
  bottom: '0deg',
  left: '90deg',
  right: '270deg',
  'top-left': '135deg',
  'top-right': '225deg',
  'bottom-left': '45deg',
  'bottom-right': '315deg',
};

const LINEAR_STOPS = {
  minimal: '18%',
  narrow: '33%',
  medium: '58%',
  wide: '85%',
  full: '115%',
};

// Modo simmetrico: il colore torna ai due estremi e il tono centrale occupa la
// fascia [a%, 100-a%]. Ampiezza «sottile» = colore raccolto ai bordi (centro
// largo), «ampia» = colore avvolgente (centro sottile).
const MIRROR_HALF = { minimal: 14, narrow: 22, medium: 32, wide: 41, full: 47 };

const RADIAL_INNERS = {
  minimal: '72%',
  narrow: '56%',
  medium: '40%',
  wide: '22%',
  full: '10%',
};

// Diametro (% del foglietto) dell’anello che segna fin dove arriva il bianco
// centrale nello sfumato radiale; specchia RADIAL_HANDLE_FRACTIONS.
const RADIAL_RING_PCT = {
  minimal: 74,
  narrow: 58,
  medium: 42,
  wide: 24,
  full: 12,
};

const BAND_FADE_ANGLES = {
  left: '90deg',
  right: '270deg',
  top: '180deg',
  bottom: '0deg',
};

export function sceneBandCss(state) {
  const palette = findByKey(BUILDER_PALETTES, state.style.palette);
  const placement = findByKey(SCENE_PLACEMENTS, state.base.scenePlacement);
  if (!placement || placement.key === 'full') {
    return '';
  }
  const extent =
    findByKey(SCENE_EXTENTS, state.base.sceneExtent) ||
    findByKey(SCENE_EXTENTS, 'third');
  const tint = palette ? palette.tint : '#e9e9e9';
  const tintSoft = palette ? palette.tintSoft : '#ffffff';
  const size = `${Math.round(extent.fraction * 100)}%`;
  const sizeCss =
    placement.axis === 'width'
      ? `width:${size}; height:100%; top:0; ${placement.key}:0;`
      : `height:${size}; width:100%; left:0; ${placement.key}:0;`;
  const angle = BAND_FADE_ANGLES[placement.key];
  const background =
    state.base.sceneFade !== false
      ? `linear-gradient(${angle}, ${tint} 0%, ${tintSoft} 70%, transparent 100%)`
      : tint;
  return `position:absolute; ${sizeCss} background:${background}; pointer-events:none;`;
}

const EXTENT_GLYPHS = {
  quarter: '¼',
  third: '⅓',
  half: '½',
  'two-thirds': '⅔',
  'three-quarters': '¾',
};

const sceneBandControls = (state) => {
  const glyph = EXTENT_GLYPHS[state.base.sceneExtent] || '⅓';
  const fadeLabel = state.base.sceneFade !== false ? 'Dissolve' : 'Netta';
  return `<button type="button" class="ai-bgb-band-handle" title="Trascina per regolare l’ampiezza">${glyph}</button><button type="button" class="ai-bgb-band-fade" data-scene-fade="${
    state.base.sceneFade !== false ? 'fade' : 'sharp'
  }">${fadeLabel}</button>`;
};

export function gradientBackgroundCss(state) {
  const { from, to } = resolvedGradientEndpoints(state);
  const usesTone = !to.isWhite;
  // Come oggi: quando la partenza «vale bianco» (bianco esplicito o eredità
  // con palette none) l'intensità agisce sull'arrivo e il primo stop è bianco
  // puro — la resa attuale resta identica per ogni combinazione preesistente.
  const startsFromWhite = usesTone && from.isWhite;
  // `from.tint` vale '#e9e9e9' per l'eredità da palette none (isWhite lo
  // dichiara comunque bianco ai fini del prompt): quando la partenza è
  // «bianca» qui serve invece il bianco puro, sia come stop sia come estremo
  // di blend per l'intensità leggera — esattamente come rendeva il codice
  // precedente (rawFrom era già forzato a '#ffffff' in questo stesso caso).
  const rawFrom = startsFromWhite ? '#ffffff' : from.tint;
  const rawTo = to.tint;
  const blendHex = (source, target, targetWeight) => {
    const parse = (hex) =>
      /^#[0-9a-f]{6}$/i.test(hex)
        ? [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16))
        : null;
    const sourceRgb = parse(source);
    const targetRgb = parse(target);
    if (!sourceRgb || !targetRgb) {
      return source;
    }
    const channel = (index) =>
      Math.round(
        sourceRgb[index] * (1 - targetWeight) + targetRgb[index] * targetWeight
      )
        .toString(16)
        .padStart(2, '0');
    return `#${channel(0)}${channel(1)}${channel(2)}`;
  };
  const intensity = state.base.gradientIntensity || 'medium';
  // L'intensità agisce sull'estremo colorato. Con una palette è la
  // partenza; con la partenza bianca implicita è invece il tono di arrivo.
  const rawColor = startsFromWhite ? rawTo : rawFrom;
  const color =
    intensity === 'light'
      ? blendHex(rawColor, startsFromWhite ? rawFrom : rawTo, 0.52)
      : intensity === 'strong'
      ? blendHex(rawColor, '#000000', 0.1)
      : rawColor;
  const fromStop = startsFromWhite ? '#ffffff' : color;
  const toStop = startsFromWhite ? color : rawTo;
  if (state.base.direction === 'edges') {
    const inner = RADIAL_INNERS[state.base.spread] || RADIAL_INNERS.medium;
    return `radial-gradient(ellipse at center, ${toStop} ${inner}, ${fromStop} 100%)`;
  }
  const angle = GRADIENT_ANGLES[state.base.direction] || '180deg';
  if (state.base.mirror) {
    const a = MIRROR_HALF[state.base.spread] || MIRROR_HALF.medium;
    return `linear-gradient(${angle}, ${fromStop} 0%, ${toStop} ${a}%, ${toStop} ${
      100 - a
    }%, ${fromStop} 100%)`;
  }
  const stop = LINEAR_STOPS[state.base.spread] || LINEAR_STOPS.medium;
  return `linear-gradient(${angle}, ${fromStop} 0%, ${toStop} ${stop})`;
}

// Filigrana e banda parziale mostrano la thumbnail dell’asset: per la scena
// sacra si usa l’asset della figura scelta (la voce "sacred" di
// BUILDER_SCENES non ne ha una propria); una scena priva di asset non mostra
// filigrana né banda, solo la tinta di sfondo. Figura «Personalizzata»: a
// differenza di una chiave sconosciuta, qui NON si vuole il fallback di
// findSacredFigure sulla Madonna (il foglio mostrerebbe un volto che l’utente
// non ha scelto) — nessun asset, resta solo la tinta.
const sceneAssetOf = (state, scene) => {
  if (state.base.sceneKey === SACRED_SCENE_KEY) {
    if (state.base.sacredFigure === 'custom') {
      return null;
    }
    return findSacredFigure(state.base.sacredFigure).asset;
  }
  return scene.asset;
};

const elementOfCell = (state, zoneKey) =>
  state.elements.find((entry) => entry.cells.includes(zoneKey)) || null;

const elementIconOf = (element) =>
  elementIconSvg(element.itemKey, { size: 18 });

const zoneCell = (state, zone) => {
  const element = elementOfCell(state, zone.key);
  const selected = Boolean(
    state.selection && state.selection.cells.includes(zone.key)
  );
  const isLead = Boolean(element) && element.cells[0] === zone.key;
  const classes = ['ai-bgb-zone', `z-${zone.gridArea}`];
  if (element) {
    classes.push('occupied', 'grouped');
  }
  if (isLead) {
    classes.push('lead');
  }
  if (selected) {
    classes.push('sel');
  }
  let content = '';
  if (isLead) {
    content = `<span class="ai-bgb-zone-em">${elementIconOf(element)}</span>`;
  } else if (!element && !selected) {
    classes.push('free');
    content = '<span class="ai-bgb-zone-plus">+</span>';
  }
  return `<div class="${classes.join(' ')}" data-zone="${zone.key}" title="${
    zone.label
  }">${content}</div>`;
};

// Lo strumento visivo del builder: un foglietto per modalità, con i soli
// affordance del compito. `mode`:
// - 'gradient': output della sfumatura corrente;
// - 'scene': input+output della collocazione (aree, banda, maniglia);
// - 'cells': input delle zone elementi (griglia, centro riservato, scena tenue);
// - 'frame': output della cornice sul fondo corrente.
export function renderBlueprintHtml(state, { ratio, mode = 'scene' } = {}) {
  // Palette «Personalizzata» (o una chiave sconosciuta): non è nel catalogo,
  // quindi findByKey torna null. Il foglio non indovina i colori descritti a
  // parole (spec 2026-08-07-builder-custom-values-design.md): ricade sulle
  // tinte neutre di «Nessuno», le stesse già usate da sceneBandCss più sopra.
  const palette =
    findByKey(BUILDER_PALETTES, state.style.palette) ||
    findByKey(BUILDER_PALETTES, 'none');
  const frame = findByKey(FRAME_STYLES, state.frame.style);
  const aspect =
    ratio || DEFAULT_RATIOS[state.orientation] || DEFAULT_RATIOS.vertical;
  const placement = findByKey(SCENE_PLACEMENTS, state.base.scenePlacement);
  const scenePartial =
    state.base.type === 'scene' &&
    Boolean(placement) &&
    placement.key !== 'full';
  const sheetClasses = [
    'ai-bgb-sheet',
    `mode-${mode}`,
    scenePartial ? 'ess-plain' : `ess-${state.base.type}`,
    `cov-${state.frame.coverage}`,
  ];
  const scene =
    state.base.type === 'scene'
      ? findByKey(BUILDER_SCENES, state.base.sceneKey)
      : null;
  const sceneAsset = scene ? sceneAssetOf(state, scene) : null;
  const showsSceneArt = mode !== 'gradient';
  const sceneBg =
    showsSceneArt && scene && !scenePartial && sceneAsset
      ? `<div class="ai-bgb-scene-bg" style="background-image:url('${sceneAsset}')"></div>`
      : '';
  const bandFadeAngle = BAND_FADE_ANGLES[state.base.scenePlacement];
  const bandMask =
    state.base.sceneFade !== false
      ? ` -webkit-mask-image:linear-gradient(${bandFadeAngle}, #000 55%, transparent 100%); mask-image:linear-gradient(${bandFadeAngle}, #000 55%, transparent 100%);`
      : '';
  const bandArt = sceneAsset
    ? ` background-image:url('${sceneAsset}'); background-size:cover; background-position:center;${bandMask}`
    : '';
  const sceneBand =
    showsSceneArt && scene && scenePartial
      ? `<div class="ai-bgb-scene-band${
          sceneAsset ? ' has-art' : ''
        }" style="${sceneBandCss(state)}${bandArt}"></div>`
      : '';
  const sceneBandUi =
    mode === 'scene' && scene && scenePartial
      ? `<div class="ai-bgb-band-ui pos-${
          state.base.scenePlacement
        }" style="${sceneBandCss(state).replace(
          'pointer-events:none;',
          'pointer-events:none; background:none;'
        )}">${sceneBandControls(state)}</div>`
      : '';
  const placeAreas =
    mode === 'scene'
      ? SCENE_PLACEMENTS.map((entry) => {
          const active =
            entry.key === state.base.scenePlacement ? ' active' : '';
          return `<button type="button" class="ai-bgb-place-area pa-${entry.key}${active}" data-scene-placement="${entry.key}" title="${entry.label}"></button>`;
        }).join('')
      : '';
  const gradientSpots =
    mode === 'gradient'
      ? GRADIENT_DIRECTIONS.map((direction) => {
          const active =
            direction.key === state.base.direction ? ' active' : '';
          return `<button type="button" class="ai-bgb-grad-spot gs-${direction.key}${active}" data-direction="${direction.key}" title="${direction.label}">${direction.arrow}</button>`;
        }).join('')
      : '';
  // L’ampiezza si regola trascinando un fronte di transizione perpendicolare
  // al flusso: una barra ruotata come l’iso-colore per le direzioni lineari,
  // un anello concentrico per il radiale.
  let gradientHandle = '';
  if (mode === 'gradient') {
    if (state.base.direction === 'edges') {
      const pct = RADIAL_RING_PCT[state.base.spread] || RADIAL_RING_PCT.medium;
      gradientHandle = `<button type="button" class="ai-bgb-grad-ring" style="width:${pct}%; height:${pct}%;" title="Trascina il bordo per regolare l’ampiezza"></button>`;
    } else {
      const pt = gradientHandlePosition(
        state.base.direction,
        state.base.spread
      );
      const angle = GRADIENT_ANGLES[state.base.direction] || '180deg';
      gradientHandle = `<button type="button" class="ai-bgb-grad-bar" style="left:${pt.left}%; top:${pt.top}%; transform:translate(-50%,-50%) rotate(${angle});" title="Trascina la barra per regolare l’ampiezza"><i class="ai-bgb-grad-grip"></i></button>`;
    }
  }
  const containMat =
    mode === 'frame' &&
    frame &&
    frame.prompt &&
    isGeometricFrameStyle(state.frame.style) &&
    state.frame.relation === 'contain'
      ? '<div class="ai-bgb-contain-mat"><i class="c c-tl"></i><i class="c c-tr"></i><i class="c c-bl"></i><i class="c c-br"></i></div>'
      : '';
  const frameOverlay =
    mode === 'frame' && frame && frame.prompt
      ? `<div class="ai-bgb-frame-overlay ${frame.css}"><i class="c c-tl"></i><i class="c c-tr"></i><i class="c c-bl"></i><i class="c c-br"></i></div>`
      : '';
  const grid =
    mode === 'cells'
      ? `<div class="ai-bgb-grid">
      ${BUILDER_ZONES.map((zone) => zoneCell(state, zone)).join('')}
      <div class="ai-bgb-center"><span>Foto e testi</span></div>
    </div>`
      : '';
  const gradientCss =
    state.base.type === 'gradient'
      ? ` background:${gradientBackgroundCss(state)};`
      : '';
  const style = `aspect-ratio:${aspect}; --bgb-tint:${palette.tint}; --bgb-tint-soft:${palette.tintSoft};${gradientCss}`;
  return `<div class="${sheetClasses.join(' ')}" style="${style}">
    ${sceneBg}
    ${sceneBand}
    ${sceneBandUi}
    ${containMat}
    ${frameOverlay}
    ${placeAreas}
    ${gradientSpots}
    ${gradientHandle}
    ${grid}
  </div>`;
}
